import { useState } from "react";
import { Alert, Box, MenuItem, TextField, Tooltip, Typography } from "@mui/material";

import {
  useGetPdfJobSsnUpdaterQuery,
  useSetPdfJobSsnSourceMutation,
} from "features/pdfTools/pdfTools";
import { noSourceHint, sourceCandidateLabel } from "../sourceSsnChoice";

export interface SplitSourceSsnFieldProps {
  jobId: string;
  vendor: string;
  canEdit: boolean;
  readOnlyReason: string | null;
  /** A check or an SSN Updater run is in flight: the file stays as it is. */
  busy: boolean;
}

function apiMessage(error: unknown, fallback: string): string {
  const data = (error as { data?: { message?: string } } | undefined)?.data;
  return data?.message || fallback;
}

/**
 * The Download major job whose SSN file the saved folders are linked into - the
 * Source SSN file an upload job reading them chooses too. Filled in by the server
 * when one fits; another choice checks the folders against that file at once.
 */
export default function SplitSourceSsnField({
  jobId,
  vendor,
  canEdit,
  readOnlyReason,
  busy,
}: SplitSourceSsnFieldProps) {
  const { currentData } = useGetPdfJobSsnUpdaterQuery({ jobId, includeEntries: false });
  const [setSource, { isLoading: saving }] = useSetPdfJobSsnSourceMutation();
  const [error, setError] = useState<string | null>(null);
  const readiness = currentData?.readiness ?? null;
  if (!readiness) return null;

  const candidates = readiness.source_candidates ?? [];
  const chosen = readiness.source_major_job?.id ?? null;
  const disabledReason = !canEdit
    ? (readOnlyReason ?? "Read-only access.")
    : busy
      ? "Wait for the check or the SSN Updater run to finish."
      : "";

  const handleChange = async (value: string) => {
    if (!value) return;
    setError(null);
    try {
      await setSource({ jobId, majorJobId: Number(value) }).unwrap();
    } catch (failure) {
      setError(apiMessage(failure, "The Source SSN file could not be changed."));
    }
  };

  return (
    <Box sx={{ mb: 1 }}>
      <Tooltip title={disabledReason}>
        <span>
          <TextField
            select
            size="small"
            label="Source SSN file"
            value={chosen == null ? "" : String(chosen)}
            onChange={(e) => void handleChange(e.target.value)}
            disabled={Boolean(disabledReason) || saving || candidates.length === 0}
            helperText={
              candidates.length === 0
                ? noSourceHint(vendor)
                : "The SSN file of a Download major job of this client - the folders are linked into it, and an upload job reading them chooses it as its Source SSN file."
            }
            SelectProps={{ displayEmpty: true }}
            sx={{ minWidth: { xs: "100%", md: 420 } }}
          >
            <MenuItem value="" disabled>
              <em>Not chosen</em>
            </MenuItem>
            {candidates.map((candidate) => (
              <MenuItem key={candidate.major_job_id} value={String(candidate.major_job_id)}>
                {sourceCandidateLabel(candidate)}
              </MenuItem>
            ))}
          </TextField>
        </span>
      </Tooltip>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      )}
    </Box>
  );
}
